import { Component, ViewChild } from '@angular/core';
import {
  AbstractBlModal,
  BlReferenceGridComponent,
  ModalButtonType,
} from '@blcloud/bl-ng-ui-component';
import {
  DateEra,
  DateTimeUtils,
  DateTimeOutput,
  DateTimePadding,
  DateSeparator,
  StringUtils,
} from '@blcloud/bl-common';
import { IWebReserve, IReceptionInfo, IBizNegoInfo, ITicketSetting, ICustomerInfo } from '@blcloud/bl-datamodel';
import { WebReserveStatus, WebReserveStatusMap } from '@blcloud/bl-datamodel/enum/webreserve/web-reserve-status';
import { PrivCorpDiv } from '@blcloud/bl-datamodel/enum/customer/priv-corp-div';
import {
  isNil as _isNil,
  isUndefined as _isUndefined,
  padStart as _padStart,
  padEnd as _padEnd,
  find as _find,
  isEmpty as _isEmpty,
} from 'lodash';
import { WebReserveDetailEntity } from './web-reserve-detail.entity';
import { WebReserveDetailService } from './web-reserve-detail.service';
import * as _moment from 'moment';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';

/**
 * Web予約詳細モーダル
 *
 */
@Component({
  selector: 'app-web-reserve-detail',
  templateUrl: './web-reserve-detail.component.html',
  styleUrls: ['./web-reserve-detail.component.scss']
})
export class WebReserveDetailComponent extends AbstractBlModal {

  /** 参照用グリッド */
  @ViewChild('refGrid') refGrid: BlReferenceGridComponent;

  /** モーダルウィンドウボタンタイプ */
  readonly modalButtonType = ModalButtonType;

  /** Web予約ステータス */
  readonly WebReserveStatus = WebReserveStatus;

  /** @ignore 画面エンティティ */
  _entity = new WebReserveDetailEntity();

  /** @ignore Web予約ステータス */
  _webReserveStatus: string;

  /** @ignore メールアドレス */
  _mailto = 'mailto:';

  /** @ignore キャンセルのみ */
  _cancelOnly: boolean;

  /** @ignore 組織名 */
  _organizationName: string;

  /** @ignore チケット設定リスト */
  _ticketSettingList: ITicketSetting[] = [];

  /** クーポン管理サービス */
  _isValidCouponManage: boolean;

  /**
   * コンストラクタ.
   */
  constructor(
    private service: WebReserveDetailService,
  ) {
    super();
  }

  /**
   * @override
   */
  getResults(): any {
    return {};
  }

  /**
   * 表示後初期化処理.
   */
  onShown() {
    this.showLoading();
    this.afterShow();
  }

  /**
   * @override
   */
  afterShow() {
    // ローディング開始
    this.showLoading();
    // Web予約情報を取得
    ObservableForkJoin(
      this.service.getWebReserve(this.data.blTenantId, this.data.webReserveNum),
      this.service.getCouponMangeService(),
    ).subscribe(responses => {
      this._isValidCouponManage = responses[1];
      // 予約ステータスが「予約確定取消」の場合
      if (responses[0].webReserveStatus === WebReserveStatus.ConfirmedCancel) {
        // 受付情報のキー作成
        const reception: IReceptionInfo = {
          productCode: responses[0].receptionKey.productCode,
          receptOrganizationCode: responses[0].receptionKey.receptOrganizationCode,
          receptionNumPrefix: responses[0].receptionKey.receptionNumPrefix,
          receptionNum: responses[0].receptionKey.receptionNum,
          registerYearMonth: responses[0].receptionKey.registerYearMonth,
        };
        // 受付情報を取得
        ObservableForkJoin(
          this.service.getReception(reception),
          this.service.getTicketSettingList(responses[0].ticketCodeList),
          this.service.getCustomerintroducerrequest(responses[0].introducerCode),
        ).subscribe(res => {
          // 商談情報
          const negoInfo = res[0].bizNegoInfoList[0];
          // クーポン情報
          this._ticketSettingList = res[1];
          // 予約詳細の設定
          this.setReserveDetail(responses[0], this.data.organizationName, res[2], negoInfo);
          // ローディング終了
          this.hideLoading();
        });
      } else {
        ObservableForkJoin(
          this.service.getTicketSettingList(responses[0].ticketCodeList),
          this.service.getCustomerintroducerrequest(responses[0].introducerCode)
        ).subscribe(
          res => {
            // クーポン情報
            this._ticketSettingList = res[0];
            // 予約詳細の設定
            this.setReserveDetail(responses[0], this.data.organizationName, res[1]);
            // ローディング終了
            this.hideLoading();
          }
        );
      }
    });
  }

  /**
   * 予約詳細を設定する
   *
   * @param webReserve Web予約情報
   * @param organizationName 組織名
   * @param negoInfo 商談情報
   */
  private setReserveDetail(webReserve: IWebReserve, organizationName: string, customerInfo: ICustomerInfo, negoInfo?: IBizNegoInfo): void {
    // 予約ステータス
    this._webReserveStatus = webReserve.webReserveStatus;
    this._entity.webReserveStatus = WebReserveStatusMap[webReserve.webReserveStatus];
    // 組織名
    this._organizationName = organizationName;
    // 予約日時
    if (!_isNil(webReserve.eventStartDate)) {
      const date = webReserve.eventStartDate + StringUtils.paddingLeft(webReserve.eventStartTime, 4);
      this._entity.reserveDateStartTime = this.dateFormat(
        _moment(date, 'YYYY-MM-DDHHmm').format('YYYY-MM-DD[T]HH:mm:00'));
    }
    // 予約番号
    this._entity.webReserveDispNum = webReserve.webReserveDispNum;
    // お名前
    if (webReserve.privCorpDiv === PrivCorpDiv.Corporate && !_isNil(webReserve.customerName1)) {
      this._entity.customerName = StringUtils.concat(
        webReserve.customerName1,
        '　',
        webReserve.customerName2);
    } else if (webReserve.privCorpDiv !== PrivCorpDiv.Corporate && !_isNil(webReserve.customerFamilyName)) {
      this._entity.customerName = StringUtils.concat(
        webReserve.customerFamilyName,
        '　',
        webReserve.customerGivenName);
    }
    // フリガナ
    if (webReserve.privCorpDiv === PrivCorpDiv.Corporate && !_isNil(webReserve.customerNameKana1)) {
      this._entity.customerNameKana = StringUtils.concat(
        webReserve.customerNameKana1,
        '　',
        webReserve.customerNameKana2);
    } else if (webReserve.privCorpDiv !== PrivCorpDiv.Corporate && !_isNil(webReserve.customerFamilyNameKana)) {
      this._entity.customerNameKana = StringUtils.concat(
        webReserve.customerFamilyNameKana,
        '　',
        webReserve.customerGivenNameKana);
    }
    // 電話番号
    this._entity.contactTelInformation = !_isNil(webReserve.contactInfoTelList[0]) &&
      !_isUndefined(webReserve.contactInfoTelList[0]) ?
      webReserve.contactInfoTelList[0].contactInformation : '';
    // メールアドレス
    this._mailto += !_isNil(webReserve.contactInfoEmailList[0]) &&
      !_isUndefined(webReserve.contactInfoEmailList[0]) ?
      webReserve.contactInfoEmailList[0].contactInformation : '';
    this._entity.contactEmailInformation = !_isNil(webReserve.contactInfoEmailList[0]) &&
      !_isUndefined(webReserve.contactInfoEmailList[0]) ?
      webReserve.contactInfoEmailList[0].contactInformation : '';
    // 住所
    this._entity.address = StringUtils.concat(
      webReserve.postalCode ? '〒' + webReserve.postalCode : '',
      '　',
      webReserve.customerAddressStateCity,
      webReserve.customerAddressStreet,
      webReserve.customerAddressBuildingName
    );
    // 電話連絡可能な時間帯
    if (webReserve.contactEnableTimeStart && webReserve.contactEnableTimeEnd) {
      // 開始時間、終了時間どちらも入力されている場合
      this._entity.contactEnableTime = StringUtils.concat(
        this.strIns(
          StringUtils.paddingLeft(webReserve.contactEnableTimeStart, 4), 2, ':') +
        '　～　' +
        this.strIns(
          StringUtils.paddingLeft(webReserve.contactEnableTimeEnd, 4), 2, ':'));
    } else if (!webReserve.contactEnableTimeStart && webReserve.contactEnableTimeEnd) {
      // 開始時間が入力されていない場合
      this._entity.contactEnableTime = StringUtils.concat(
        '　～　' +
        this.strIns(
          StringUtils.paddingLeft(webReserve.contactEnableTimeEnd, 4), 2, ':'));
    } else if (webReserve.contactEnableTimeStart && !webReserve.contactEnableTimeEnd) {
      // 終了時間が入力されていない場合
      this._entity.contactEnableTime = StringUtils.concat(
        this.strIns(
          StringUtils.paddingLeft(webReserve.contactEnableTimeStart, 4), 2, ':') +
        '　～　');
    } else {
      // どちらも入力されていない場合は表示しない
      this._entity.contactEnableTime = '';
    }
    // サービス
    this._entity.itemServiceName = webReserve.itemServiceName;
    // お車のナンバー
    if (Number(webReserve.numPlateSeriesSpecifNum) !== 0) {
      this._entity.registrateNum = StringUtils.concat(
        _padEnd(webReserve.vcleInspRegistOfficeName, 4, '　'),
        ' ',
        _padEnd(webReserve.numPlateClassNum, 3, ' '),
        ' ',
        _padEnd(webReserve.numPlateKana, 1, '　'),
        ' ',
        _padStart(webReserve.numPlateSeriesSpecifNum.toString(), 4, ' '));
    }
    // メーカー
    this._entity.blVehicleMakerFullWFixNm = webReserve.blVehicleMakerFullWFixNm;
    // 車種
    this._entity.vehicleModelFixFullWName = webReserve.vehicleModelFixFullWName;
    // 車検満了日
    if (!_isNil(webReserve.vcleInspDueDate)) {
      this._entity.vehicleInspectionExpirationDate = DateTimeUtils.format(
        webReserve.vcleInspDueDate,
        DateTimeOutput.YMD, DateTimePadding.NONE, DateSeparator.JP, DateEra.JP);
    }
    // 代車
    this._entity.replacementVehicle = webReserve.repVehicleRequestDivEnumName;
    // クーポン
    this._entity.ticketCode = '';
    this._entity.ticketName = '';
    if (!_isEmpty(webReserve.ticketCodeList)) {
      this._entity.ticketCode = webReserve.ticketCodeList[0];
      const ticket = _find(this._ticketSettingList, ticketSetting =>
        ticketSetting.ticketCode === webReserve.ticketCodeList[0]
      );
      this._entity.ticketName = !_isNil(ticket) ? ticket.ticketName : '該当なし';
    }
    // 紹介コード
    this._entity.introducerCode = !_isNil(webReserve.introducerCode) ? webReserve.introducerCode : '';
    // 紹介者名
    this._entity.introducerName = '';
    if (!_isNil(webReserve.introducerFamilyName) && !_isNil(webReserve.introducerGivenName)) {
      this._entity.introducerName = webReserve.introducerFamilyName + '　' + webReserve.introducerGivenName;
    } else if (!_isNil(webReserve.introducerFamilyName)) {
      this._entity.introducerName = webReserve.introducerFamilyName;
    } else if (!_isNil(webReserve.introducerGivenName)) {
      this._entity.introducerName = webReserve.introducerGivenName;
    }
    // 紹介者名カナ
    this._entity.introducerNameKana = '';
    if (!_isNil(webReserve.introducerFamilyNameKana) && !_isNil(webReserve.introducerGivenNameKana)) {
      this._entity.introducerNameKana = webReserve.introducerFamilyNameKana + '　' + webReserve.introducerGivenNameKana;
    } else if (!_isNil(webReserve.introducerFamilyNameKana)) {
      this._entity.introducerNameKana = webReserve.introducerFamilyNameKana;
    } else if (!_isNil(webReserve.introducerGivenNameKana)) {
      this._entity.introducerNameKana = webReserve.introducerGivenNameKana;
    }
    // 紹介者コードから取得した顧客情報
    if (!_isNil(customerInfo)) {
      this._entity.customerCode = customerInfo.customerCode;
      this._entity.customerName1 = customerInfo.customerName1;
      this._entity.customerNameKana1 = customerInfo.customerNameKana1;
    } else {
      this._entity.customerCode = 0;
      this._entity.customerName1 = '';
      this._entity.customerNameKana1 = '';
    }
    // ご質問・ご希望など（備考）
    this._entity.note = webReserve.dealsMemoText;

    if (negoInfo) {
      this._cancelOnly = true;
      // キャンセル日
      this._entity.cancelDate = this.dateFormat(negoInfo.cancelDate);
      // キャンセル理由
      this._entity.cancelReason = negoInfo.cancelReason;
    } else {
      this._cancelOnly = false;
    }
  }

  /**
   * 文字列の指定位置に特定の文字を挿入
   *
   * @param str 文字列
   * @param idx 指定位置
   * @param val 特定の文字
   */
  private strIns(str: string, idx: number, val: string): string {
    return str.slice(0, idx) + val + str.slice(idx);
  }

  /**
   * 日時フォーマット処理
   * @param date 2000-01-31T00:00:00
   */
  dateFormat(date: string) {
    if (date === null) {
      return '';
    }
    return DateTimeUtils.format(
      DateTimeUtils.parseDate(date),
      DateTimeOutput.YMD_LT, DateTimePadding.ZERO, DateSeparator.JP, DateEra.JP);
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/web-reserve-detail/web-reserve-detail.component.ts